import {
  Badge,
  FlexBox,
  Icon,
  Page,
  Spinner,
  TabItem,
  Tabs,
} from '@dixa/lib-shared-habla';
import { useCallback, useMemo } from 'react';
import { Outlet, useMatch } from 'react-router-dom';

import { rolesApi } from 'apis/roles';
import { AppLink } from 'components/appLink';
import { AppPathWithParams } from 'utils/url/appPaths';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SettingsAgentsRoleTab } from '../../settingsAgentsRoles.types';

export const SettingsAgentsRolesEditView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const { roleType, roleId } = useAppPathParams(
    '/settings/agents/roles/:roleType/:roleId',
  );

  const { role, isFetching } = rolesApi.useReadRoleWithScopesQuery(
    {
      roleId,
      isSystemRole: roleType === 'system',
    },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        role: data?.data.role,
      }),
    },
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const tab = value as SettingsAgentsRoleTab;

      navigate({
        path: `/settings/agents/roles/:roleType/:roleId/${tab}`,
        params: {
          path: { roleType, roleId },
        },
      });
    },
    [navigate, roleId, roleType],
  );

  const tabs = useMemo(
    (): (TabItem & { id: SettingsAgentsRoleTab })[] => [
      { id: 'permissions', label: 'Role permissions' },
      { id: 'agents', label: 'Agents' },
    ],
    [],
  );

  const agentsMatch = useMatch(
    '/settings/agents/roles/:roleType/:roleId/agents' satisfies AppPathWithParams,
  );

  const selectedTab: SettingsAgentsRoleTab = agentsMatch
    ? 'agents'
    : 'permissions';

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path="/settings/agents" style={{ color: 'inherit' }}>
              Agents
            </AppLink>

            <Icon name="angle-right" />

            <AppLink path="/settings/agents/roles" style={{ color: 'inherit' }}>
              Roles
            </AppLink>

            <Icon name="angle-right" />

            {role?.name}

            {role && (
              <Badge
                variant={roleType === 'system' ? 'secondary' : 'primary'}
                size="small"
              >
                {roleType === 'system' ? 'System role' : 'Custom role'}
              </Badge>
            )}
          </FlexBox>
        }
      />

      <Page.Tabs>
        <Tabs selectedId={selectedTab} tabs={tabs} onChange={handleTabChange} />
      </Page.Tabs>

      <Page.Body>
        {isFetching ? (
          <FlexBox
            alignItems="center"
            justifyContent="center"
            height="100%"
            width="100%"
          >
            <Spinner />
          </FlexBox>
        ) : (
          <Outlet />
        )}
      </Page.Body>
    </Page>
  );
};
